import { Helmet } from 'react-helmet-async';
import { H1, H2, H3, Body } from '../components/ui/Typography';

export default function CookiePolicy() {
  return (
    <>
      <Helmet>
        <title>RentFi | Cookie Policy</title>
        <meta 
          name="description" 
          content="Learn about how RentFi uses cookies to improve your experience on our real estate investment platform, including essential cookies, analytics cookies, and how to manage your cookie preferences." 
        />
        <meta 
          name="keywords" 
          content="RentFi cookie policy, cookie settings, website cookies, privacy settings, cookie preferences" 
        />
        <link rel="canonical" href="https://rentfi.io/cookie-policy" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <H1 className="text-primary mb-4">Cookies Policy</H1>
            <p className="text-gray-500 mb-8">Last Updated: January 30, 2025</p>

            {/* Introduction */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">1. Introduction</H2>
              <Body className="text-gray-600">
                This Cookies Policy explains how RentFi ("Company", "we", "our", "us") uses cookies and similar technologies on our website (www.rentfi.io) and platform. By using our Services, you consent to the use of cookies as described in this policy.
              </Body>
            </div>

            {/* What Are Cookies */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">2. What Are Cookies?</H2>
              <Body className="text-gray-600 mb-4">
                Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by:
              </Body>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Remembering your preferences</li>
                <li>Understanding how you use our platform</li>
                <li>Improving platform security</li>
                <li>Enabling certain platform functions</li>
              </ul>
            </div>

            {/* Types of Cookies */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">3. Types of Cookies We Use</H2>
              
              <div className="space-y-6">
                <div>
                  <H3 className="text-gray-900 mb-2">3.1 Essential Cookies</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Required for platform functionality</li>
                    <li>Enable core features and security measures</li>
                    <li>Cannot be disabled as they are necessary for platform operation</li>
                    <li>Used for wallet connections and transaction verification</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">3.2 Performance Cookies</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Collect anonymous information about platform usage</li>
                    <li>Help us understand user behavior</li>
                    <li>Provide analytics on platform performance</li>
                    <li>Enable us to improve user experience</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">3.3 Functionality Cookies</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Remember user preferences</li>
                    <li>Enhance user experience</li>
                    <li>Store language preferences</li>
                    <li>Maintain user session information</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">3.4 Third-Party Cookies</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Set by external services we use</li>
                    <li>May include analytics providers</li>
                    <li>Used by decentralized exchange interfaces</li>
                    <li>Enable blockchain interactions</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Cookie Management */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">4. Cookie Management</H2>
              
              <div className="space-y-6">
                <div>
                  <H3 className="text-gray-900 mb-2">4.1 Browser Settings</H3>
                  <Body className="text-gray-600 mb-2">
                    You can manage cookies through your browser settings:
                  </Body>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Block all cookies</li>
                    <li>Delete existing cookies</li>
                    <li>Allow cookies from specific sites</li>
                    <li>Set cookie preferences</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">4.2 Individual Cookie Control</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Essential cookies cannot be disabled</li>
                    <li>Performance cookies can be opted out</li>
                    <li>Functionality cookies can be controlled</li>
                    <li>Third-party cookies can be managed separately</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Specific Cookie Uses */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">5. Specific Cookie Uses</H2>
              
              <div className="space-y-6">
                <div>
                  <H3 className="text-gray-900 mb-2">5.1 Platform Function Cookies</H3>
                  <Body className="text-gray-600 mb-2">Used for:</Body>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Wallet connectivity</li>
                    <li>Transaction signing</li>
                    <li>Distribution tracking</li>
                    <li>User authentication</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">5.2 Analytics Cookies</H3>
                  <Body className="text-gray-600 mb-2">Track:</Body>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>User behavior</li>
                    <li>Platform usage patterns</li>
                    <li>Feature engagement</li>
                    <li>Performance metrics</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">5.3 Security Cookies</H3>
                  <Body className="text-gray-600 mb-2">Ensure:</Body>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Secure transactions</li>
                    <li>Platform integrity</li>
                    <li>User authentication</li>
                    <li>Fraud prevention</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Cookie Lifespan */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">6. Cookie Lifespan</H2>
              
              <div className="space-y-6">
                <div>
                  <H3 className="text-gray-900 mb-2">6.1 Session Cookies</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Temporary cookies</li>
                    <li>Deleted when browser closes</li>
                    <li>Used for platform functionality</li>
                    <li>Required for active sessions</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">6.2 Persistent Cookies</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Remain after browser closes</li>
                    <li>Have set expiration dates</li>
                    <li>Store user preferences</li>
                    <li>Enable enhanced functionality</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Third-Party Services */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">7. Third-Party Services</H2>
              <Body className="text-gray-600 mb-4">
                We use third-party services that may set cookies:
              </Body>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Analytics providers</li>
                <li>Blockchain interfaces</li>
                <li>Security services</li>
                <li>Performance monitoring tools</li>
              </ul>
            </div>

            {/* Updates to This Policy */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">8. Updates to This Policy</H2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>We may update this policy periodically</li>
                <li>Changes will be posted on our platform</li>
                <li>Continued use implies acceptance</li>
                <li>Users will be notified of significant changes</li>
              </ul>
            </div>

            {/* Your Choices */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">9. Your Choices</H2>
              
              <div className="space-y-6">
                <div>
                  <H3 className="text-gray-900 mb-2">9.1 Cookie Settings</H3>
                  <Body className="text-gray-600 mb-2">You can:</Body>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Modify browser settings</li>
                    <li>Delete existing cookies</li>
                    <li>Block future cookies</li>
                    <li>Manage third-party cookies</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">9.2 Impact of Blocking Cookies</H3>
                  <Body className="text-gray-600 mb-2">Disabling cookies may:</Body>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Affect platform functionality</li>
                    <li>Limit certain features</li>
                    <li>Impact user experience</li>
                    <li>Reduce platform performance</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">10. Contact Information</H2>
              <Body className="text-gray-600">
                For questions about our Cookies Policy:<br />
                <a 
                  href="mailto:support@rentfi.helpcrunch-mail.com" 
                  className="text-secondary hover:text-secondary-dark"
                  aria-label="Email RentFi support team with questions about cookie policy"
                >
                  support@rentfi.helpcrunch-mail.com
                </a>
              </Body>
            </div>

            {/* Additional Information */}
            <div>
              <H2 className="text-primary mb-4">11. Additional Information</H2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Cookies do not store personal information</li>
                <li>We do not use cookies for advertising</li>
                <li>Cookie data is encrypted where applicable</li>
                <li>We regularly review our cookie usage</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}